import React, { useState, useEffect, useMemo } from "react";
import "./SupportTicketPage.css";
import { getSupportTickets } from "../../services/supportTicketService";
import { SupportTicket } from "../../types/supportTicket";

// Component & Icon Imports
import SupportTicketModal from "../../compoonents/SupportTicketModal/SupportTicketModal";
import emptyIcon from "../../assets/images/support.png";
import { FiEye } from "react-icons/fi";
import PageLoader from "../../compoonents/PageLoader";
import { PageSearchBar } from "../../compoonents/PageToolbar";
import prevIcon from "../../assets/images/previcon.png";
import nextIcon from "../../assets/images/nexticon.png";

const SupportTicketPage: React.FC = () => {
  // --- STATE ---
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [activeStatus, setActiveStatus] = useState<"All" | "PENDING" | "ANSWERED" | "CLOSED">("All");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  // --- DATA FETCHING ---
  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      setError(null);
      try {
        // Pass Search and Status to the service (if backend supports it)
        const response = await getSupportTickets(currentPage, 10, searchTerm, activeStatus);
        
        // Handle response structure
        // Adjust this based on your exact API response structure (e.g. response.data.results or response.results)
        const results = response.results || response.data?.results || [];
        setTickets(results);
        
        const pagination = response.pagination || response.data?.pagination || {};
        setTotalPages(pagination.totalPages || 1);
        setTotalItems(pagination.totalItems || 0);

      } catch (err) {
        setError("Failed to load support tickets.");
      } finally {
        setLoading(false);
      }
    };

    // Debounce to prevent flashing while typing
    const timer = setTimeout(() => {
      fetchTickets();
    }, 500);

    return () => clearTimeout(timer);
  }, [currentPage, searchTerm, activeStatus]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeStatus]);

  // --- CLIENT-SIDE FALLBACK FILTERING ---
  // This ensures the list updates immediately even if backend ignores params
  const visibleTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      // 1. Status Filter
      // API statuses are lowercase (pending | answered | closed).
      if (activeStatus !== "All" && String(ticket.status).toUpperCase() !== activeStatus) {
        return false;
      }

      // 2. Search Filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const name = ticket.name?.toLowerCase() || "";
        const email = ticket.email?.toLowerCase() || "";
        const id = ticket.ticketId?.toLowerCase() || "";
        const title = ticket.title?.toLowerCase() || "";
        
        return name.includes(searchLower) || 
               email.includes(searchLower) || 
               id.includes(searchLower) || 
               title.includes(searchLower);
      }
      return true;
    });
  }, [activeStatus, searchTerm, tickets]);

  // Handlers
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage((p) => p + 1); };
  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage((p) => p - 1); };
  
  const handleViewDetails = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  // Render Helper to keep main return clean
  const renderContent = () => {
    if (loading && !tickets.length) {
      return <PageLoader message="Loading support tickets..." />;
    }

    if (error) {
      return <div className="page-error" style={{textAlign:'center', padding:'40px', color:'red'}}>{error}</div>;
    }

    if (visibleTickets.length === 0) {
      return (
        <div className="support-ticket-page page--empty">
           {searchTerm ? (
             <div style={{textAlign: 'center'}}>
                <h3>No results for "{searchTerm}"</h3>
             </div>
           ) : (
             <>
                <img src={emptyIcon} alt="No tickets" />
                <h2>No tickets recorded yet</h2>
             </>
           )}
        </div>
      );
    }

    return (
      <>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Ticket ID</th>
                <th>Date Created</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleTickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td data-label="Name">{ticket.name}</td>
                  <td data-label="Email">{ticket.email}</td>
                  <td data-label="Ticket ID">{ticket.ticketId}</td>
                  <td data-label="Date Created">{new Date(ticket.createdAt).toLocaleDateString()}</td>
                  <td data-label="Status">
                    <span className={`status-badge status-${ticket.status.toLowerCase()}`}>
                      {ticket.status.charAt(0) + ticket.status.slice(1).toLowerCase()}
                    </span>
                  </td>
                  <td data-label="Action">
                    <div className="table-action-buttons">
                      <button
                        type="button"
                        className="table-action-btn"
                        title="View ticket"
                        aria-label="View ticket"
                        onClick={() => handleViewDetails(ticket)}
                      >
                        <FiEye />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <footer className="page-footer">
          <div className="pagination-info">
            Showing page {currentPage} of {totalPages} ({totalItems} total)
          </div>
          <div className="pagination-controls">
            <button onClick={handlePrevPage} disabled={currentPage === 1}>
              <img src={prevIcon} alt="Previous" />
            </button>
            <button onClick={handleNextPage} disabled={currentPage >= totalPages}>
              <img src={nextIcon} alt="Next" />
            </button>
          </div>
        </footer>
      </>
    );
  };

  return (
    <>
      <div className="support-ticket-page">
        {error && (
          <div
            className="coming-soon-banner"
            style={{
              background: '#FEF3C7',
              border: '1px solid #F59E0B',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '16px',
              color: '#92400E',
              fontSize: '14px',
            }}
          >
            Could not load support tickets. Please refresh to try again.
          </div>
        )}
        <header className="page-controls-header">
          {/* TABS act as the primary filter here */}
          <div className="filter-tabs">
            {(["All", "PENDING", "ANSWERED", "CLOSED"] as const).map((tab) => (
              <button
                key={tab}
                className={activeStatus === tab ? "active" : ""}
                onClick={() => setActiveStatus(tab)}
              >
                {tab === "All" ? "All Tickets" : tab.charAt(0) + tab.slice(1).toLowerCase()}
              </button>
            ))}
          </div>

          <div className="search-and-filter">
            <PageSearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search name, ID..."
            />
          </div>
        </header>

        {renderContent()}
      </div>

      {selectedTicket && (
        <SupportTicketModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          ticket={selectedTicket}
        />
      )}
    </>
  );
};

export default SupportTicketPage;